import { CheckCheck, CircleAlert, CircleCheck, Waypoints } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';

function OverviewCard({
  title,
  value,
  icon: Icon,
  className,
  valueSuffix,
  isLoading,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  className?: string;
  valueSuffix?: string;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <Card className="min-w-0 border-none p-0 shadow-none">
        <CardContent className="flex items-center gap-3 p-0">
          <Skeleton className="h-11 w-11 shrink-0 rounded-lg sm:h-14 sm:w-14" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3 w-24 sm:h-4" />
            <Skeleton className="h-6 w-16 sm:h-7" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-w-0 border-none p-0 shadow-none">
      <CardContent className="flex items-center gap-3 p-0">
        <div className={cn('shrink-0 rounded-lg bg-primary/10 p-2.5 text-primary sm:p-3', className)}>
          <Icon className="h-6 w-6 sm:h-8 sm:w-8" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-muted-foreground sm:text-sm">{title}</p>
          <p className="text-xl font-bold tabular-nums sm:text-2xl">
            {value}
            {valueSuffix}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function ToolOverviewCards({
  totalCalls,
  successfulCalls,
  failedCalls,
  isLoading,
}: {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
      <OverviewCard
        title={t('TOTAL_REQUESTS')}
        className="bg-cyan-500/10 text-primary-500 dark:bg-primary-500/15 dark:text-primary-400"
        value={totalCalls}
        icon={Waypoints}
        isLoading={isLoading}
      />
      <OverviewCard
        title={t('SUCCESS_RATE')}
        value={totalCalls ? Math.round((successfulCalls / totalCalls) * 100) : 0}
        valueSuffix="%"
        icon={CircleCheck}
        className="bg-cyan-500/10 text-cyan-500 dark:bg-cyan-500/15 dark:text-cyan-400"
        isLoading={isLoading}
      />
      <OverviewCard
        title={t('SUCCESS_REQUESTS')}
        value={successfulCalls}
        icon={CheckCheck}
        className="bg-green-500/10 text-green-600 dark:bg-green-500/15 dark:text-green-400"
        isLoading={isLoading}
      />
      <OverviewCard
        title={t('FAILED_REQUESTS')}
        value={failedCalls}
        icon={CircleAlert}
        className="bg-red-500/10 text-red-600 dark:bg-red-500/15 dark:text-red-400"
        isLoading={isLoading}
      />
    </div>
  );
}
